import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import * as Icon from 'react-feather';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { Link } from 'react-router-dom';
import Footer from '../../component/Footer/footer';
import Navbar from '../../component/Navbar/navbar';
import { CONTACT_US } from '../../graphql/mutations/contact';


export default function Contact() {
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        comments: ''
    });
    // react-google-recaptcha-v3
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Use useLazyQuery to trigger the query on submit
    const [triggerContactUs, { loading: gqlLoading, data: gqlData, error: gqlError }] = useMutation(CONTACT_US, {
        fetchPolicy: "network-only"
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [touched, setTouched] = useState({});

    // Validation helpers
    const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValid =
        form.name.trim().length > 0 && form.name.length <= 60 &&
        isEmail(form.email) &&
        form.subject.trim().length > 0 && form.subject.length <= 120 &&
        form.comments.trim().length > 0 && form.comments.length <= 2000;

    // Handle input changes

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Enforce max length
        let newValue = value;
        if (name === 'name') newValue = value.slice(0, 60);
        if (name === 'subject') newValue = value.slice(0, 120);
        if (name === 'comments') newValue = value.slice(0, 2000);
        setForm({ ...form, [name]: newValue });
    };

    const handleBlur = (e) => {
        setTouched({ ...touched, [e.target.name]: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess('');
        setError('');
        setLoading(true);
        try {

            if (!executeRecaptcha) {
                setError('reCAPTCHA not yet available. Please try again.');
                setLoading(false);
                return;
            }
            const token = await executeRecaptcha('contact_form');

            await triggerContactUs({
                variables: {
                    name: form.name,
                    email: form.email,
                    reason: form.subject,
                    details: form.comments,
                    captchaToken: token
                }
            });

        } catch (err) {
            console.error(err);
            setError('An error occurred. Please try again.');
            setLoading(false);
        }
    };

    // Handle GraphQL response
    React.useEffect(() => {
        if (gqlData && gqlData.contactUs) {
            setSuccess('Your message has been sent!');
            setForm({ name: '', email: '', subject: '', comments: '' });
            setTouched({});
            setLoading(false);
        } else if (gqlError) {
            // Try to extract a specific error message from GraphQL error
            let customMessage = 'An error occurred. Please try again.';
            if (gqlError.graphQLErrors && gqlError.graphQLErrors.length > 0) {
                const backendMsg = gqlError.graphQLErrors[0].message;
                if (backendMsg) customMessage = backendMsg;
            }
            setError(customMessage);
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [gqlData, gqlError]);

    // Use GraphQL loading state if available
    const effectiveLoading = loading || gqlLoading;

    return (
        <>
            <Navbar navClass="nav-light" />

            <section className="relative table w-full py-20 bg-black bg-center bg-no-repeat bg-cover bg-[url('../../assets/images/bg3.png')]">
                <div className="absolute inset-0"></div>
                <div className="container relative">
                    <div className="grid grid-cols-1 pb-8 text-center mt-10">
                        <h3 className="text-4xl md:leading-normal tracking-wide leading-normal font-medium text-white">Contact Us</h3>
                        <p className='text-slate-300 mt-2 text-xl' style={{ maxWidth: 420, margin: "0 auto" }}>Got a question, suggestion, or bug report? We're here to help.</p>
                    </div>
                </div>

                <div className="absolute text-center z-10 bottom-5 start-0 end-0 mx-3 mb-2">
                    <ul className="tracking-[0.5px] mb-0 inline-flex space-x-1">
                        <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out text-white/50 hover:text-white"><Link to="/index">Home</Link></li>
                        <li className="inline-block text-base text-white/50 mx-0.5 ltr:rotate-0 rtl:rotate-180"><MdKeyboardArrowRight className="text-xl" /></li>
                        <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out text-white" aria-current="page">Contact Us</li>
                    </ul>
                </div>
            </section>

            <section className="relative md:py-12 py-16">

                <div className="container relative">
                    <div className="grid md:grid-cols-12 grid-cols-1 items-center gap-[30px]">

                        <div className="lg:col-span-12 md:col-span-12">
                            <div className="lg:ms-5">
                                <div className="bg-secondary rounded-2xl p-6">
                                    <h3 className="mb-6 text-2xl leading-normal font-medium">Get in touch !</h3>

                                    <p className='text-slate-300 mb-8'>We're here to help with any questions, feedback, or technical issues you may encounter.</p>

                                    <form onSubmit={handleSubmit}>
                                        <div className="grid lg:grid-cols-12 lg:gap-6">
                                            <div className="lg:col-span-6 mb-5">
                                                <div className="text-start">
                                                    <label htmlFor="name" className="font-semibold">Your Name:</label>
                                                    <div className="form-icon relative mt-2">
                                                        <Icon.User className="size-4 absolute top-3 start-4"></Icon.User>
                                                        <input name="name" id="name" type="text" maxLength={60} className="form-input ps-11 w-full py-2 px-3 h-10 bg-black-0 rounded outline-none border border-gray-200 focus:border-indigo-600 dark:border-gray-800 dark:focus:border-indigo-600 focus:ring-0" placeholder="Name :" value={form.name} onChange={handleChange} onBlur={handleBlur} required />
                                                        {touched.name && form.name.length === 0 && <div className="text-red-500 text-xs mt-1">Name is required.</div>}
                                                        {touched.name && form.name.length > 60 && <div className="text-red-500 text-xs mt-1">Max 60 characters.</div>}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="lg:col-span-6 mb-5">
                                                <div className="text-start">
                                                    <label htmlFor="email" className="font-semibold">Your Email:</label>
                                                    <div className="form-icon relative mt-2">
                                                        <Icon.Mail className="size-4 absolute top-3 start-4"></Icon.Mail>
                                                        <input name="email" id="email" type="email" className="form-input ps-11 w-full py-2 px-3 h-10 bg-black-0 rounded outline-none border border-gray-200 focus:border-indigo-600 dark:border-gray-800 dark:focus:border-indigo-600 focus:ring-0" placeholder="Email :" value={form.email} onChange={handleChange} onBlur={handleBlur} required />
                                                        {touched.email && !isEmail(form.email) && <div className="text-red-500 text-xs mt-1">Enter a valid email.</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1">
                                            <div className="mb-5">
                                                <div className="text-start">
                                                    <label htmlFor="subject" className="font-semibold">Your Question:</label>
                                                    <div className="form-icon relative mt-2">
                                                        <Icon.Book className="size-4 absolute top-3 start-4"></ Icon.Book>
                                                        <input name="subject" id="subject" maxLength={120} className="form-input ps-11 w-full py-2 px-3 h-10 bg-black-0 rounded outline-none border border-gray-200 focus:border-indigo-600 dark:border-gray-800 dark:focus:border-indigo-600 focus:ring-0" placeholder="Subject :" value={form.subject} onChange={handleChange} onBlur={handleBlur} required />
                                                        <div className="flex justify-between text-xs mt-1">
                                                            {touched.subject && form.subject.length === 0 && <span className="text-red-500">Subject is required.</span>}
                                                            <span className={form.subject.length > 120 ? "text-red-500" : "text-gray-400"}>{form.subject.length}/120</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-5">
                                                <div className="text-start">
                                                    <label htmlFor="comments" className="font-semibold">Your Comment:</label>
                                                    <div className="form-icon relative mt-2">
                                                        <Icon.MessageCircle className="size-4 absolute top-3 start-4"></Icon.MessageCircle>
                                                        <textarea name="comments" id="comments" maxLength={2000} className="form-input ps-11 w-full py-2 px-3 h-28 bg-black-0 rounded outline-none border border-gray-200 focus:border-indigo-600 dark:border-gray-800 dark:focus:border-indigo-600 focus:ring-0" placeholder="Message :" value={form.comments} onChange={handleChange} onBlur={handleBlur} required></textarea>
                                                        <div className="flex justify-between text-xs mt-1">
                                                            {touched.comments && form.comments.length === 0 && <span className="text-red-500">Message is required.</span>}
                                                            <span className={form.comments.length > 2000 ? "text-red-500" : "text-gray-400"}>{form.comments.length}/2000</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Invisible reCAPTCHA v3: no widget needed */}
                                       
                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <button type="submit" id="submit" name="send"
                                                className="py-2 px-8 font-semibold tracking-wide align-middle duration-500 text-base text-center bg-blue-500 hover:bg-blue-400 text-white rounded-2xl"
                                                disabled={effectiveLoading || !isValid}>
                                                {effectiveLoading ? 'Sending...' : 'Submit'}
                                            </button>
                                        </div>
                                        {success && <div className="text-green-500 mt-3">{success}</div>}
                                        {error && <div className="text-red-500 mt-3">{error}</div>}
                                        <p className="mb-0 mt-2 text-gray-400" style={{ fontSize: 14 }}>Email: contact@thewide.com</p>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>

    );
}
